import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";

/**
 * Msg 251 §6 · Add-to-squad modal + trigger button. Never adds a user
 * automatically — sends a pending invite that the invitee must accept.
 *
 * Usage:
 *   <AddToSquadButton targetUserId="u_123" targetHandle="foo" eventId="e_1" />
 *   <AddToSquadButton targetHandle="foo" />     // profile page
 *   <AddToSquadButton eventId="e_1" />          // event page (invites nobody
 *                                               // yet, but shares context)
 *
 * On event pages with no `targetUserId`, the modal lists a handle input so
 * staff/members can pick who to invite. Same modal, one place.
 */
const YELLOW = "#FFD60A";
const INK = "#1A1A30";

export default function AddToSquadButton({ targetUserId, targetHandle, eventId, testIdPrefix = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [squads, setSquads] = useState(null);
  const [busy, setBusy] = useState(false);
  const [handleInput, setHandleInput] = useState(targetHandle || "");
  const [newSquadName, setNewSquadName] = useState("");

  useEffect(() => {
    if (!open || !user) return;
    setSquads(null);
    client.get("/squads/mine").then((r) => setSquads(r.data.squads || []))
      .catch(() => setSquads([]));
  }, [open, user]);

  if (!user) return null;
  // Never let a member add themselves.
  if (targetUserId && targetUserId === user.user_id) return null;

  const send = async (squadId) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = {};
      if (targetUserId) body.invitee_user_id = targetUserId;
      else if (handleInput) body.invitee_handle = handleInput.trim().replace(/^@+/, "");
      if (eventId) body.event_id = eventId;
      if (!body.invitee_user_id && !body.invitee_handle) {
        toast.error("who are we inviting?");
        setBusy(false);
        return;
      }
      const r = await client.post(`/squads/${squadId}/invite`, body);
      if (r.data.already_member) toast.success("already in the squad");
      else if (r.data.duplicate) toast.success("invite already sent");
      else toast.success("invite sent");
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replace(/_/g, " ") : "invite failed");
    } finally { setBusy(false); }
  };

  const createAndInvite = async () => {
    const name = (newSquadName || "").trim();
    if (!name) { toast.error("name the squad first"); return; }
    setBusy(true);
    try {
      const r = await client.post("/squads", { name });
      const squadId = r.data.squad.id;
      await send(squadId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replace(/_/g, " ") : "could not create squad");
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid={`${testIdPrefix}add-to-squad-btn`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs lowercase"
              style={{ background: "transparent", border: `1px solid ${YELLOW}`,
                       color: "var(--ww-text)", fontFamily: "Poppins", fontWeight: 600,
                       cursor: "pointer" }}>
        <Users size={13} strokeWidth={2} /> add to squad
      </button>
      {open && (
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 70,
                      display: "grid", placeItems: "center" }}>
          <div style={{ background: "var(--ww-surface)", color: "#FFF", borderRadius: 14,
                        padding: 20, width: "92%", maxWidth: 400,
                        border: "1px solid #2F2F52" }}
               data-testid={`${testIdPrefix}add-to-squad-modal`}>
            <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
              add to squad
            </p>
            <p style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)", marginBottom: 14 }}>
              they'll get an invite. no one joins a squad without accepting.
            </p>
            {!targetUserId && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontFamily: "Arial", fontSize: 11, color: "var(--ww-text-muted)",
                                 textTransform: "uppercase", letterSpacing: "0.14em" }}>
                  invitee @handle
                </label>
                <input value={handleInput}
                       onChange={(e) => setHandleInput(e.target.value)}
                       placeholder="@handle"
                       data-testid={`${testIdPrefix}add-to-squad-handle`}
                       style={{ width: "100%", marginTop: 4, padding: "10px 12px",
                                borderRadius: 10, background: "var(--ww-sunken)",
                                border: "1px solid #2F2F52", color: "#FFF",
                                fontFamily: "Arial", fontSize: 14 }} />
              </div>
            )}
            {squads === null ? (
              <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>loading your squads…</p>
            ) : squads.length === 0 ? (
              <div>
                <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)", marginBottom: 8 }}>
                  no squads yet. name one to start:
                </p>
                <input value={newSquadName}
                       onChange={(e) => setNewSquadName(e.target.value)}
                       placeholder="my crew"
                       data-testid={`${testIdPrefix}add-to-squad-newname`}
                       style={{ width: "100%", padding: "10px 12px",
                                borderRadius: 10, background: "var(--ww-sunken)",
                                border: "1px solid #2F2F52", color: "#FFF",
                                fontFamily: "Arial", fontSize: 14, marginBottom: 10 }} />
                <button disabled={busy}
                        data-testid={`${testIdPrefix}add-to-squad-create-and-invite`}
                        onClick={createAndInvite}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 999,
                                 background: YELLOW, color: INK, border: 0,
                                 fontFamily: "Poppins", fontWeight: 700, cursor: busy ? "not-allowed" : "pointer" }}>
                  create squad and invite
                </button>
              </div>
            ) : (
              <ul style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 260, overflowY: "auto" }}>
                {squads.map((s) => (
                  <li key={s.id}>
                    <button disabled={busy}
                            data-testid={`${testIdPrefix}add-to-squad-pick-${s.id}`}
                            onClick={() => send(s.id)}
                            style={{ width: "100%", display: "flex", alignItems: "center",
                                     justifyContent: "space-between", padding: "10px 12px",
                                     borderRadius: 10, background: "var(--ww-sunken)",
                                     border: "1px solid #2F2F52", color: "#FFF",
                                     fontFamily: "Poppins", fontSize: 14, cursor: "pointer" }}>
                      <span style={{ fontWeight: 600 }}>{s.name}</span>
                      <span style={{ fontSize: 11, color: "var(--ww-text-muted)" }}>add</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => setOpen(false)}
                    data-testid={`${testIdPrefix}add-to-squad-cancel`}
                    style={{ marginTop: 14, width: "100%", padding: "8px",
                             background: "transparent", color: "var(--ww-text-muted)",
                             border: 0, cursor: "pointer", fontFamily: "Arial", fontSize: 12 }}>
              cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
